export default function Footer() {
  const footerLinks = {
    product: ['Features', 'Solutions', 'Industries', 'Pricing', 'Integrations', 'API', 'What\'s New', 'Changelog'],
    company: ['About Us', 'Careers', 'Contact', 'Partners', 'Press', 'Blog'],
    resources: ['Help Center', 'Documentation', 'Video Tutorials', 'Webinars', 'Community', 'Status'],
    legal: ['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'GDPR', 'Security'],
  };

  return (
    <footer className="relative bg-secondary-950 text-white overflow-hidden">
      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20 pb-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-10 mb-14">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <a href="#" className="inline-block mb-4">
              <img
                src="https://storage.readdy-site.link/project_files/97de57b0-ad1c-46f5-89d0-1b80c93be7d3/a456ce04-b604-4221-a93a-e099f3eb0476_compressed_API-logo-1.webp"
                alt="DGFlow"
                className="h-12 w-auto"
              />
            </a>
            <p className="text-sm text-white/40 leading-relaxed mb-6 max-w-xs">
              The all-in-one WhatsApp Business Automation Platform. Turn every conversation into business growth.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-facebook-fill', href: '#' },
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-linkedin-fill', href: '#' },
                { icon: 'ri-youtube-fill', href: '#' },
                { icon: 'ri-twitter-x-fill', href: '#' },
              ].map(social => (
                <a
                  key={social.icon}
                  href={social.href}
                  className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-primary-500/20 hover:text-primary-400 transition-all duration-300 cursor-pointer"
                  rel="nofollow"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          <div>
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-4">Product</h4>
            <ul className="space-y-2.5">
              {footerLinks.product.map(link => (
                <li key={link}>
                  <a href="#" className="text-sm text-white/50 hover:text-white transition-colors duration-200">{link}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-4">Company</h4>
            <ul className="space-y-2.5">
              {footerLinks.company.map(link => (
                <li key={link}>
                  <a href="#" className="text-sm text-white/50 hover:text-white transition-colors duration-200">{link}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-4">Resources</h4>
            <ul className="space-y-2.5">
              {footerLinks.resources.map(link => (
                <li key={link}>
                  <a href="#" className="text-sm text-white/50 hover:text-white transition-colors duration-200">{link}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-4">Legal</h4>
            <ul className="space-y-2.5">
              {footerLinks.legal.map(link => (
                <li key={link}>
                  <a href="#" className="text-sm text-white/50 hover:text-white transition-colors duration-200">{link}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-t border-white/5 pt-10 mb-10">
          <div className="max-w-xl">
            <h4 className="text-sm font-semibold text-white mb-2">Stay in the loop</h4>
            <p className="text-xs text-white/40 mb-4">Get the latest WhatsApp automation tips, product updates, and industry insights.</p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d9dlv4go4b7spa5iiqv0"
              method="POST"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                if (honeypot) {
                  const successMsg = document.getElementById('newsletter-success');
                  if (successMsg) successMsg.classList.remove('hidden');
                  form.reset();
                  return;
                }
                const formData = new FormData(form);
                const urlParams = new URLSearchParams();
                formData.forEach((value, key) => {
                  if (key !== 'phone_alt') {
                    urlParams.append(key, value as string);
                  }
                });
                try {
                  const res = await fetch(form.action, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: urlParams.toString(),
                  });
                  const text = await res.text();
                  let parsed: Record<string, unknown> = {};
                  try { parsed = JSON.parse(text); } catch { /* ignore */ }
                  const code = (parsed?.code as string) || '';
                  if (res.ok && code === 'OK') {
                    const successMsg = document.getElementById('newsletter-success');
                    if (successMsg) successMsg.classList.remove('hidden');
                    form.reset();
                  }
                } catch { /* silently handle */ }
              }}
            >
              {/* Honeypot */}
              <div className="contact-honeypot-wrap">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Your email address"
                  className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary-400/50 transition-all"
                />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Subscribe
                </button>
              </div>
              <div id="newsletter-success" className="hidden mt-3 text-sm text-primary-400">
                <i className="ri-check-line mr-1"></i>Subscribed successfully!
              </div>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/25">
          <p>&copy; {new Date().getFullYear()} DGFlow. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white/40 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white/40 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-white/40 transition-colors">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}